/**
 * Marks the deliberate failures QA fires to validate Error Tracking, so they
 * are separable from real user crashes.
 *
 * The acceptance criterion is "QA can verify the setup with test failures
 * without polluting real-user metrics". A staff-account filter alone does not
 * achieve that: staff also use Panda normally, and their genuine crashes are
 * exactly the ones you want visible while validating. What distinguishes a
 * drill is not WHO threw it but WHERE — the drill surfaces exist only to throw.
 *
 * Deriving the marker from the route rather than from a flag someone has to
 * remember to set means it also covers AUTOCAPTURED exceptions (an unhandled
 * rejection has no call site to pass a flag at) and cannot be left switched on
 * by accident.
 *
 * Pure string comparison, zero imports — usable from `before_send` and from
 * `instrumentation.ts` on either runtime.
 */

/** The staff-gated page QA triggers browser-side drills from. */
export const CLIENT_DRILL_ROUTE = "/debug/error-tracking";
/** The staff-gated route handler QA triggers server-side drills from. */
export const SERVER_DRILL_ROUTE = "/api/debug/error-tracking";

const DRILL_ROUTES = new Set<string>([CLIENT_DRILL_ROUTE, SERVER_DRILL_ROUTE]);

/**
 * True when a route exists solely to throw on purpose. Exact match only: a
 * prefix test would sweep up any future real route that happens to start with
 * `/debug/error-tracking`, and quietly hide genuine failures from the
 * dashboards — the expensive direction to be wrong in.
 */
export function isErrorTrackingDrillRoute(
  route: string | null | undefined,
): boolean {
  return !!route && DRILL_ROUTES.has(route);
}
